import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { API } from "../../lib/site";

const inputCls = "w-full rounded-2xl border border-[#C8D7E4] bg-white px-4 py-3.5 text-base text-[#15579A] placeholder:text-[#15579A]/40 outline-none transition-colors focus:border-[#218FFF] focus:ring-2 focus:ring-[#218FFF]/20";

const Field = ({ f, value, onChange, prefix }) => (
  <label className={f.wide ? "sm:col-span-2" : ""}>
    <span className="mb-1.5 block text-sm font-medium text-[#15579A]">{f.label}{f.required && " *"}</span>
    {f.textarea ? (
      <textarea rows={3} className={inputCls} value={value} onChange={onChange} placeholder={f.placeholder}
        maxLength={1000} data-testid={`${prefix}-input-${f.name}`} />
    ) : (
      <input className={inputCls} value={value} onChange={onChange} placeholder={f.placeholder} required={f.required}
        type={f.type || "text"} inputMode={f.inputMode} maxLength={120} data-testid={`${prefix}-input-${f.name}`} />
    )}
  </label>
);

export const LeadForm = ({ origem, fields, submitLabel, prefix }) => {
  const empty = Object.fromEntries(fields.map((f) => [f.name, ""]));
  const [data, setData] = useState({ ...empty, website: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Marque o consentimento para enviar."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...data, origem, consentimento: true });
      setDone(true);
      setData({ ...empty, website: "" });
      setConsent(false);
    } catch (err) {
      toast.error(err.response?.status === 429 ? "Muitas tentativas. Tente novamente mais tarde ou fale no WhatsApp." : "Não foi possível enviar agora. Tente novamente ou fale no WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-[24px] border border-[#C8D7E4] bg-white p-8 text-center" data-testid={`${prefix}-success`}>
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#218FFF]" strokeWidth={2.5} />
        <p className="mt-4 text-lg font-bold text-[#15579A]">Recebemos seus dados.</p>
        <p className="mt-1 text-sm text-[#15579A]/75">Nossa equipe entrará em contato pelo WhatsApp informado.</p>
        <button onClick={() => setDone(false)} className="mt-6 text-sm font-semibold text-[#196ABC] underline-offset-4 hover:underline"
          data-testid={`${prefix}-new-btn`}>Enviar outro</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-[24px] border border-[#C8D7E4] bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid={`${prefix}-form`}>
      {fields.map((f) => (
        <Field key={f.name} f={f} prefix={prefix} value={data[f.name]} onChange={(e) => setData({ ...data, [f.name]: e.target.value })} />
      ))}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true"
        value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} />
      <label className="flex items-start gap-3 text-xs leading-relaxed text-[#15579A]/80 sm:col-span-2">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[#196ABC]" data-testid={`${prefix}-consent-checkbox`} />
        Autorizo o Clube do Contemplado a usar estes dados para entrar em contato sobre esta solicitação, conforme a LGPD (Lei 13.709/2018). Posso pedir a exclusão a qualquer momento.
      </label>
      <button type="submit" disabled={loading} data-testid={`${prefix}-submit-btn`}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#196ABC] px-7 py-4 text-base font-semibold text-[#F7F5F0] transition-colors hover:bg-[#218FFF] active:bg-[#15579A] disabled:opacity-60 sm:col-span-2">
        {loading && <Loader2 className="h-5 w-5 animate-spin" />} {submitLabel}
      </button>
    </form>
  );
};
